// src/fieldProgress/trackedSource.js — IS THIS FARM'S CROP ONE WE CAN EVER MEASURE?
//
// Field Progress paces machine acres over insured acres. A farm whose machine data for a
// crop will NEVER arrive still put its whole insured acreage into the denominator, read
// "awaiting machine data" and counted toward "To review". Kevin, 2026-10-20, on the
// CY2026 soybean harvest (BOOK PACE 33% = 6,154 / 18,547): "our harvest progress is
// artificially low because we are awaiting harvest data that will never come".
//
// How those farms got onto the list: the book is every active connection PLUS every
// farm with machine work attributed to its insured ground (jd_field_ftf.farmer_id is
// the CLU OWNER). Measured live 2026-10-20, seven farms reached it with 0 connections,
// 0 own operations and 0 jd_fields — on slivers (EDMUND 1.1 ac of a neighbour's corn
// planter, DUNHAM 0.1 ac) or on custom work for a DIFFERENT crop (MCILROY triticale,
// JOHN B MARTIN broccoli). None held any 2026 soybean machine data.
//
// THE RULE. A farm is TRACKED for (crop, crop year) when
//   (a) it holds its own active John Deere connection (directly, or as a member of a
//       connected org in jd_org_farmers — the org's data files under it), OR
//   (b) planting OR harvest of THAT crop (corn grain and silage are one crop) reached
//       its insured ground this crop year in a material amount:
//         >= TRACK_MIN_AC acres AND >= TRACK_MIN_SHARE of its insured acres of the crop.
//       Planting counts because a custom planting means a custom harvest is coming.
//
// THE FLOOR IS A JUDGEMENT PLACED IN A MEASURED GAP. Over the unconnected CY2026 book,
// the largest same-crop share that is plainly NOT a machine source is R L JEFFRES corn,
// 117.3 planted of 6,538 insured (1.8%) — 6,400 acres that will never report. The
// smallest that plainly is: NAAS FARMS corn, 80.6 of 829 (9.7%, W Cleveland's planter),
// and MY-T ACRES corn, 124.5 of 1,204 (10.3%). 5% sits in the empty band between. The
// 5-acre floor is what keeps a sliver on a small farm from qualifying by share alone.
//
// An untracked crop is never dropped: it leaves the pace and the review count and is
// LISTED, with its insured acres and the reason. Claims never depend on it.
//
// CommonJS on purpose, like src/claims/harvestRule.js: api/_jd.js require()s it and
// App.jsx imports it, so the server's verdict and the screen's partition are one rule.

"use strict";

const TRACK_MIN_AC = 5;
const TRACK_MIN_SHARE = 0.05;
// Winter crops: the planting pass for crop year Y happens in the autumn of Y-1.
const FALL_CROPS = new Set(["WHEAT", "BARLEY", "TRITICALE", "RYE"]);
const PLANT_METRICS = new Set(["seed_rate", "coverage"]);
const HARVEST_METRICS = new Set(["yield"]);

function trackCropKey(c) {
  const u = String(c || "").trim().toUpperCase();
  return u === "CORN SILAGE" ? "CORN" : u;
}

// Does a machine pass on `date` count toward crop year `year` for this crop?
// Harvest: the crop year itself. Planting: the crop year, or for a winter crop
// 1 Aug of the prior year through 31 Jul of the crop year.
function trackWindowHolds(crop, metric, date, year) {
  const d = String(date || "").slice(0, 10);
  const y = Number(year);
  if (!d || !Number.isFinite(y)) return false;
  const k = trackCropKey(crop);
  if (HARVEST_METRICS.has(metric)) return d >= `${y}-01-01` && d <= `${y}-12-31`;
  if (PLANT_METRICS.has(metric)) {
    if (FALL_CROPS.has(k)) return d >= `${y - 1}-08-01` && d <= `${y}-07-31`;
    return d >= `${y}-01-01` && d <= `${y}-12-31`;
  }
  return false;
}

// jd_field_ftf rows (this farm's insured ground) -> { CROP: { plant, harvest } } acres.
function machineAcresByCrop(rows, year) {
  const out = {};
  for (const r of rows || []) {
    if (!trackWindowHolds(r.crop, r.metric, r.operation_date, year)) continue;
    const k = trackCropKey(r.crop);
    if (!k) continue;
    const m = (out[k] ||= { plant: 0, harvest: 0 });
    const ac = Number(r.acres) || 0;
    if (HARVEST_METRICS.has(r.metric)) m.harvest += ac; else m.plant += ac;
  }
  return out;
}

const r1 = (x) => Math.round((Number(x) || 0) * 10) / 10;

// The verdict for one (farm, crop). `machine` is machineAcresByCrop's output for the farm.
function trackedFor({ ownConnection, crop, insuredAcres, machine, year }) {
  const k = trackCropKey(crop);
  if (ownConnection) return { tracked: true, basis: "connection", reason: null };
  const m = (machine || {})[k] || { plant: 0, harvest: 0 };
  // Planting and harvest of the same ground are the same acres, so the larger, never the sum.
  const ac = Math.max(m.plant || 0, m.harvest || 0);
  const ins = Number(insuredAcres) || 0;
  const share = ins > 0 ? ac / ins : 0;
  if (ac >= TRACK_MIN_AC && share >= TRACK_MIN_SHARE) {
    return { tracked: true, basis: "same_crop_machine", reason: null,
             evidence: { plant_acres: r1(m.plant), harvest_acres: r1(m.harvest), insured_acres: r1(ins) } };
  }
  const nm = k.toLowerCase();
  const other = Object.entries(machine || {})
    .filter(([c, v]) => c !== k && Math.max(v.plant || 0, v.harvest || 0) >= 1)
    .map(([c, v]) => `${c.toLowerCase()} ${r1(Math.max(v.plant, v.harvest))} ac`);
  const yr = year ? ` for crop year ${year}` : "";
  let reason = ac >= 0.05
    ? `No John Deere connection; only ${r1(ac)} ac of ${nm} machine work reached its insured ground${yr}`
    : `No John Deere connection, and no ${nm} planting or harvest has reached its insured ground${yr}`;
  if (other.length) reason += ` (other crops: ${other.slice(0, 3).join(", ")})`;
  return { tracked: false, basis: null, reason };
}

// Stamp every area of one farm card. Insured acres per crop are summed across counties.
function stampAreasTracked(areas, { ownConnection, machine, year }) {
  const insByCrop = {};
  for (const a of areas || []) {
    const k = trackCropKey(a.crop);
    insByCrop[k] = (insByCrop[k] || 0) + (Number(a.insured_acres) || 0);
  }
  for (const a of areas || []) {
    const v = trackedFor({ ownConnection, crop: a.crop, insuredAcres: insByCrop[trackCropKey(a.crop)], machine, year });
    a.tracked = v.tracked;
    a.tracked_basis = v.basis;
    a.tracked_reason = v.reason;
  }
  return areas;
}

// The screen's half. Splits filtered farm cards into the ones the pace is computed over
// and the not-tracked list. An area with no `tracked` stamp (a server that predates this)
// stays tracked — the old behaviour, never a silent drop.
function partitionTracked(farms) {
  const tracked = [], untracked = [];
  for (const f of farms || []) {
    const areas = f.areas || [];
    const keep = areas.filter((a) => a.tracked !== false);
    const drop = areas.filter((a) => a.tracked === false);
    if (keep.length) tracked.push(drop.length ? { ...f, areas: keep } : f);
    if (drop.length) {
      const insured = drop.reduce((s, a) => s + (Number(a.insured_acres) || 0), 0);
      const reasons = [...new Set(drop.map((a) => a.tracked_reason).filter(Boolean))];
      untracked.push({ ...f, areas: drop, insured_acres: insured, reason: reasons.join("; ") || "No machine data source" });
    }
  }
  return { tracked, untracked };
}

// ⚠ A CARD'S "COMPLETE" MUST DESCRIBE THE AREAS THE CARD SHOWS. Kevin, 2026-10-20: "Alan
// Panek progress bar should be green if he is done with soybeans" — SOYBEANS view, 122/122
// ac, 100%, yet yellow "In progress", because `farm_harvest_complete` is a FARM flag over
// every crop and his corn is not done. So: when the card shows ALL of the farm's areas, the
// farm flag stands (it also carries the no-activity-for-N-days test); when a filter (crop,
// type, state, county) or the tracked partition has narrowed it, completion is every shown
// area's own `complete` — the SAME per-area flag the farm flag is built from (hClaimWatch's
// COMPLETE over fields + ACRE_FLOOR), never a second threshold.
function cardHarvestComplete(f, areasTotal) {
  const areas = (f && f.areas) || [];
  if (!areas.length) return false;
  if (areasTotal != null && areas.length === areasTotal) return f.farm_harvest_complete === true;
  return areas.every((a) => a.complete === true);
}

// ── EXCLUDED FROM THE HARVEST PACE, BY AN AGENT ─────────────────────────────────────────
// Kevin, 2026-10-26, on the CY2026 soybean harvest: "can we add a way to maybe uncheck or
// not include some of these farms who we are not getting harvest data for? It's
// artificially driving the harvest percent completed data down." RED DAWN 0/1,182 ac,
// KOCH LIBRA 0/658, VERRATTI 0/877 — CONNECTED farms (so trackedFor keeps them), whose
// harvest of THAT crop is not arriving (custom harvester, another make of combine, a sync
// that stopped). Whether a 0 is real or missing data is a judgement only the agent can
// make, so this is a stored HUMAN decision per (farm, crop year, crop) — never derived.
//
// An excluded crop leaves the pace — numerator AND denominator, which is the whole point —
// and is LISTED with its planted acres, never dropped. The headline states how many farms
// and acres are out, so a higher % is never silently a narrower one. A claim flag on an
// excluded area is still counted (the same rule as an untracked one).
// `excludedKeys` is a Set of paceExclusionKey(); empty or missing = nothing excluded.
const { harvestDenominator } = require("./harvestComplete.js");

function paceExclusionKey(farmerId, cropYear, crop) {
  return `${farmerId || ""}|${Number(cropYear) || ""}|${trackCropKey(crop)}`;
}

function partitionPaceExcluded(farms, excludedKeys) {
  const keys = excludedKeys instanceof Set ? excludedKeys : new Set(excludedKeys || []);
  if (!keys.size) return { paced: farms || [], excluded: [] };
  const paced = [], excluded = [];
  for (const f of farms || []) {
    const areas = f.areas || [];
    const isOut = (a) => !!f.farmer_id && keys.has(paceExclusionKey(f.farmer_id, f.year, a.crop));
    const keep = areas.filter((a) => !isOut(a));
    const drop = areas.filter(isOut);
    if (keep.length) paced.push(drop.length ? { ...f, areas: keep } : f);
    if (drop.length > 0) {
      excluded.push({ ...f, areas: drop,
        crops: [...new Set(drop.map((a) => trackCropKey(a.crop)))],
        planted_acres: drop.reduce((s, a) => s + harvestDenominator(a), 0),
        harvested_acres: drop.reduce((s, a) => s + (Number(a.harvested_acres) || 0), 0) });
    }
  }
  return { paced, excluded };
}

// What the headline must say about what it left out: distinct farms and planted acres.
function paceExclusionSummary(excluded) {
  const farmIds = new Set((excluded || []).map((f) => f.farmer_id || f.key));
  const acres = (excluded || []).reduce((s, f) => s + (Number(f.planted_acres) || 0), 0);
  return { farms: farmIds.size, acres };
}

// DISPLAY-ONLY HINT, NEVER AN EXCLUSION. A connected farm's crop reads 0 harvested while
// its harvest data is plainly going somewhere else, or nowhere:
//   - no harvest pass on the farm at all this crop year, OR
//   - the farm's last harvest pass is more than HINT_STALE_DAYS old, OR
//   - the farm HAS harvested acres on another crop (so its passes are arriving, for that).
// `allAreas` is the farm's UNFILTERED area list (the crop filter must not hide the other
// crop that proves passes are arriving). Returns null or { reason }.
const HINT_STALE_DAYS = 21;
const HINT_ZERO_AC = 0.5;
function noHarvestDataHint({ crop, allAreas, lastHarvestDate, daysSinceHarvest }) {
  const k = trackCropKey(crop);
  const areas = allAreas || [];
  const mine = areas.filter((a) => trackCropKey(a.crop) === k);
  if (!mine.length) return null;
  const harvested = mine.reduce((s, a) => s + (Number(a.harvested_acres) || 0), 0);
  if (harvested >= HINT_ZERO_AC) return null;
  const nm = k.toLowerCase();
  if (!lastHarvestDate) return { reason: `No ${nm} harvest and no harvest pass of any crop on this farm this year` };
  const others = [...new Set(areas.filter((a) => trackCropKey(a.crop) !== k && (Number(a.harvested_acres) || 0) >= HINT_ZERO_AC)
    .map((a) => trackCropKey(a.crop).toLowerCase()))];
  const ago = daysSinceHarvest != null ? ` (${daysSinceHarvest}d ago)` : "";
  if (daysSinceHarvest != null && daysSinceHarvest > HINT_STALE_DAYS)
    return { reason: `No ${nm} harvest; last harvest pass of any crop was ${lastHarvestDate}${ago}` };
  if (others.length)
    return { reason: `No ${nm} harvest, but ${others.join(", ")} harvest is arriving (last pass ${lastHarvestDate}${ago})` };
  return null;
}

module.exports = {
  paceExclusionKey, partitionPaceExcluded, paceExclusionSummary, noHarvestDataHint, HINT_STALE_DAYS,
  cardHarvestComplete,
  TRACK_MIN_AC, TRACK_MIN_SHARE, FALL_CROPS,
  trackCropKey, trackWindowHolds, machineAcresByCrop, trackedFor, stampAreasTracked, partitionTracked,
};
